'use client';

import React from 'react';
import Link from 'next/link';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-full font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-orange-800  text-white hover:bg-orange-700 dark:bg-orange-100 dark:text-black font-light",
        outline: "border border-orange-800 text-white text-orange-800 font-light hover:bg-orange-50 font-heading",
        link: "text-orange-800 text-white  font-heading font-light",
      },
      size: {
        sm: "h-9 px-4 py-2 text-sm",
        md: "h-10 px-6 py-2.5 text-base",
        lg: "h-12 px-6 md:px-8 md:py-3 text-base md:text-lg",
        xl: "h-18 px-8 md:px-10 md:py-4 text-base md:text-xl",
      },
    },
    compoundVariants: [
      {
        variant: "link",
        size: "sm",
        className: "h-auto p-0 text-sm",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  href?: string;
  className?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, href, children, ...props }, ref) => {
    if (href) {
      return (
        <Link
          href={href}
          className={cn(buttonVariants({ variant, size, className }))}
        >
          {children}
        </Link>
      );
    }
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants }; 